import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Label } from './label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './popover';
import { Settings2 } from 'lucide-react';

/**
 * Selector de columnas visibles para grillas
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array de columnas: [{key: 'patente', label: 'Patente', defaultVisible: true}, ...]
 * @param {Array} props.visibleColumns - Array de keys de columnas visibles
 * @param {Function} props.onVisibilityChange - Callback cuando cambia la visibilidad: (visibleKeys) => void
 * @param {string} props.storageKey - Key para guardar en localStorage (opcional)
 */
export function ColumnSelector({ columns, visibleColumns, onVisibilityChange, storageKey }) {
    const [open, setOpen] = useState(false);

    // Cargar desde localStorage al montar
    useEffect(() => {
        if (storageKey) {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    onVisibilityChange(parsed);
                } catch (e) {
                    console.error('Error parsing saved columns:', e);
                }
            }
        }
    }, [storageKey]);

    const handleToggle = (key, checked) => {
        let newVisible;
        if (checked) {
            newVisible = [...visibleColumns, key];
        } else {
            newVisible = visibleColumns.filter(k => k !== key);
        }
        onVisibilityChange(newVisible);
        
        // Guardar en localStorage
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(newVisible));
        }
    };

    const handleSelectAll = () => {
        const allKeys = columns.map(c => c.key);
        onVisibilityChange(allKeys);
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(allKeys));
        }
    };

    const handleSelectNone = () => {
        // Mantener al menos las columnas obligatorias
        const required = columns.filter(c => c.required).map(c => c.key);
        onVisibilityChange(required.length > 0 ? required : [columns[0]?.key]);
        if (storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(required.length > 0 ? required : [columns[0]?.key]));
        }
    };

    const handleReset = () => {
        const defaultVisible = columns.filter(c => c.defaultVisible !== false).map(c => c.key);
        onVisibilityChange(defaultVisible);
        if (storageKey) {
            localStorage.removeItem(storageKey);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="column-selector-btn">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Columnas
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-4" align="end">
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">Columnas visibles</h4>
                        <span className="text-xs text-muted-foreground">
                            {visibleColumns.length}/{columns.length}
                        </span>
                    </div>
                    
                    <div className="flex gap-2 text-xs">
                        <button 
                            onClick={handleSelectAll}
                            className="text-primary hover:underline"
                        >
                            Todas
                        </button>
                        <span className="text-muted-foreground">|</span>
                        <button 
                            onClick={handleSelectNone}
                            className="text-primary hover:underline"
                        >
                            Mínimo
                        </button>
                        <span className="text-muted-foreground">|</span>
                        <button 
                            onClick={handleReset}
                            className="text-primary hover:underline"
                        >
                            Restablecer
                        </button>
                    </div>

                    <div className="max-h-64 overflow-y-auto space-y-2">
                        {columns.map((col) => (
                            <div key={col.key} className="flex items-center space-x-2">
                                <Checkbox
                                    id={`col-${col.key}`}
                                    checked={visibleColumns.includes(col.key)}
                                    onCheckedChange={(checked) => handleToggle(col.key, checked)}
                                    disabled={col.required && visibleColumns.length === 1}
                                />
                                <Label 
                                    htmlFor={`col-${col.key}`} 
                                    className="text-sm cursor-pointer flex-1"
                                >
                                    {col.label}
                                    {col.required && <span className="text-muted-foreground ml-1">*</span>}
                                </Label>
                            </div>
                        ))}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default ColumnSelector;
